import React from 'react'
import intl from 'react-intl-universal'
import { connect, DispatchProp } from 'react-redux'
import {
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  FormFeedback,
  Label,
  Input,
  Button,
} from 'reactstrap'

import { getCustomEmbed, updateCustomEmbed } from '~/redux/settingsReducer'
import { EllipsisReduxStore } from '~/types'
import { SelectIconEmbed } from './IconSelection/SelectIcon'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./CustomEmbedSettingsModal.styles.scss')

interface Props {
  visible: boolean
  subtype?: string
  toggle: () => void
}

type ReduxProps = {
  customEmbed?: EllipsisReduxStore['settings']['customEmbed']['subtype']
} & DispatchProp

interface State {
  subtype: {
    value: string
    error: string
  }
  caption: {
    value: string
    error: string
  }
  view: {
    value: string
    error: string
  }
  edit: {
    value: string
    error: string
  }
  search: {
    value: string
    error: string
  }
  icon: {
    value: string
    error: string
  }
}

interface Action {
  type: 'FIELD_CHANGED' | 'FIELD_ERROR'
  payload: {
    name: string
    value: string
  }
}

const DEFAULT_LOAD_TIMEOUT = 10000

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'FIELD_CHANGED':
      return {
        ...state,
        [action.payload.name]: {
          error: '',
          value: action.payload.value,
        },
      }
    case 'FIELD_ERROR':
      return {
        ...state,
        [action.payload.name]: {
          ...state[action.payload.name],
          error: action.payload.value,
        },
      }
    default:
      throw new Error('Unrecognized action type')
  }
}

function handleBoltIcon(icon: string) {
  return icon !== 'bolt' ? icon : 'sun-o'
}

// Validating the scheme name, ://, and no spaces or double quotes
// is usually sufficient for URLs.
const urlPattern = '^(ftp|http|https)://[^ "]+$'

function CustomEmbedSettingsModal(props: Props & ReduxProps) {
  const mode = props.customEmbed ? 'update' : 'add'

  const [state, dispatch] = React.useReducer(reducer, {
    subtype: {
      value: props.subtype || '',
      error: '',
    },
    caption: {
      value: props.customEmbed ? props.customEmbed.caption || '' : '',
      error: '',
    },
    view: {
      value: props.customEmbed ? props.customEmbed.view : '',
      error: '',
    },
    edit: {
      value: props.customEmbed ? props.customEmbed.edit : '',
      error: '',
    },
    search: {
      value: props.customEmbed ? props.customEmbed.search : '',
      error: '',
    },
    icon: {
      value: handleBoltIcon(props?.customEmbed?.icon ?? 'square'),
      error: '',
    },
  })

  const handleFieldChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget

    dispatch({
      type: 'FIELD_CHANGED',
      payload: {
        name,
        value,
      },
    })
  }

  const validateFields = (formElements: HTMLFormControlsCollection) => {
    let hasError = false

    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i]

      if (element instanceof HTMLInputElement) {
        if (element.validity.valueMissing) {
          dispatch({
            type: 'FIELD_ERROR',
            payload: {
              name: element.name,
              value: intl.get('CustomEmbedSettingsModal.errors.required'),
            },
          })
          hasError = true
        } else if (element.validity.patternMismatch) {
          dispatch({
            type: 'FIELD_ERROR',
            payload: {
              name: element.name,
              value: intl.get('CustomEmbedSettingsModal.errors.invalidUrl'),
            },
          })
          hasError = true
        }
      }
    }

    return hasError
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const hasError = validateFields(event.currentTarget.elements)

    if (!hasError) {
      props.dispatch(
        updateCustomEmbed({
          subtype: state.subtype.value,
          icon: state.icon.value,
          embed: {
            caption: state.caption.value,
            view: state.view.value,
            edit: state.edit.value,
            search: state.search.value,
            loadTimeout: DEFAULT_LOAD_TIMEOUT,
          },
        })
      )
      props.toggle()
    }
  }

  return (
    <Modal
      wrapClassName="bootstrap4"
      size="lg"
      className={styles.root}
      centered
      isOpen
      scrollable
      toggle={props.toggle}
    >
      <Form noValidate onSubmit={handleSubmit}>
        <ModalBody>
          <h4 className="title adsv3-h2">
            {intl.get(`CustomEmbedSettingsModal.header.${mode}`)}
          </h4>
          <div className="group">
            <Label for="caption">
              {intl.get('CustomEmbedSettingsModal.fields.caption.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-name-input"
              className="input-primary"
              name="caption"
              id="caption"
              value={state.caption.value}
              invalid={Boolean(state.caption.error)}
              onChange={handleFieldChange}
              readOnly={mode === 'update'}
              required
            />
            <FormFeedback>{state.caption.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="subtype">
              {intl.get('CustomEmbedSettingsModal.fields.subtype.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-display-type-input"
              className="input-primary"
              name="subtype"
              id="subtype"
              value={state.subtype.value}
              invalid={Boolean(state.subtype.error)}
              onChange={handleFieldChange}
              readOnly={mode === 'update'}
              required
            />
            <FormFeedback>{state.subtype.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="search">
              {intl.get('CustomEmbedSettingsModal.fields.search.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-search-input"
              className="input-primary"
              name="search"
              id="search"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.search.placeholder'
              )}
              value={state.search.value}
              invalid={Boolean(state.search.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.search.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="view">
              {intl.get('CustomEmbedSettingsModal.fields.view.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-view-input"
              className="input-primary"
              name="view"
              id="view"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.view.placeholder'
              )}
              value={state.view.value}
              invalid={Boolean(state.view.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.view.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="edit">
              {intl.get('CustomEmbedSettingsModal.fields.edit.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-edit-input"
              className="input-primary"
              name="edit"
              id="edit"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.edit.placeholder'
              )}
              value={state.edit.value}
              invalid={Boolean(state.edit.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.edit.error}</FormFeedback>
          </div>
          <div className="divider"></div>
          <SelectIconEmbed
            value={state.icon.value}
            onIconSelected={(value) => {
              const eventSimulator: unknown = {
                currentTarget: {
                  name: 'icon',
                  value: value,
                },
              }
              handleFieldChange(
                eventSimulator as React.FormEvent<HTMLInputElement>
              )
            }}
          />
        </ModalBody>
        <ModalFooter className="footer">
          <Button
            data-testid="settings-custom-embed-cancel-btn"
            color="secondary"
            className="btn-override-secondary"
            outline
            onClick={props.toggle}
          >
            {intl.get('CustomEmbedSettingsModal.buttons.cancel')}
          </Button>
          <Button
            data-testid="settings-custom-embed-add-btn"
            className="btn-override-primary"
            type="submit"
            color="primary"
          >
            {intl.get(`CustomEmbedSettingsModal.buttons.${mode}`)}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default connect((state: EllipsisReduxStore, props: Props) => ({
  customEmbed: getCustomEmbed(state)[props.subtype],
}))(CustomEmbedSettingsModal)
